import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import * as React from 'react';
import Icon from 'components/Icon';
import { classNames } from 'utils/ui/css';
import { filterDealerIdOptions, isOptionAvailable } from 'utils/auth/dealerIdOptions';
import { getDealerUnavailableLabel } from 'utils/auth/dealerSelectionMessages';
import type { IDealerIdOption } from 'utils/auth/dealerResolution.types';

interface IDealerIdPickerProps {
  options: readonly IDealerIdOption[];
  preselectedDealerId?: string;
  onSelect: (dealerId: string) => void;
  /** Callback refs so the caller (the dialog) can point FocusTrap's `initialFocus` at either element. */
  searchInputRef?: (element: HTMLInputElement | null) => void;
  okButtonRef?: (element: HTMLButtonElement | null) => void;
}

/**
 * Presentational searchable Dealer ID dropdown: one flat list in the given order, with no group
 * headings, an always-present search field, and an OK button that is the only way to commit a
 * selection.
 */
export default function DealerIdPicker({
  options,
  preselectedDealerId,
  onSelect,
  searchInputRef,
  okButtonRef,
}: IDealerIdPickerProps) {
  const { t } = useLingui();
  const [query, setQuery] = React.useState('');
  const [selectedDealerId, setSelectedDealerId] = React.useState<string | undefined>(() =>
    isOptionAvailable(options, preselectedDealerId) ? preselectedDealerId : undefined
  );

  const filteredOptions = filterDealerIdOptions(options, query);
  const isSelectionAvailable = isOptionAvailable(options, selectedDealerId);

  return (
    <div className="mt-2.5">
      <Combobox
        as="div"
        className="flex items-start gap-2"
        value={selectedDealerId ?? null}
        onChange={(value) => setSelectedDealerId(value ?? undefined)}
        onClose={() => setQuery('')}
      >
        {({ open }) => (
          <>
            <div className="min-w-0 flex-1">
              <div className="relative">
                <ComboboxInput
                  ref={searchInputRef}
                  data-testid="dealer-selection-combobox"
                  aria-label={t`Search Dealer ID`}
                  placeholder={t`Search Dealer ID`}
                  className="w-full rounded-md px-3 py-[9px] pr-9 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-700"
                  onChange={(event) => setQuery(event.target.value)}
                  onClick={(event) => {
                    if (!open) {
                      // Synthetic ArrowDown: the chevron toggle ignores programmatic clicks after a mouse press.
                      event.currentTarget.dispatchEvent(
                        new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true, cancelable: true })
                      );
                    }
                  }}
                />
                <ComboboxButton
                  data-testid="dealer-selection-combobox-button"
                  aria-label={t`Choose your Dealer ID`}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <Icon
                    aria-hidden="true"
                    className={classNames(
                      'ex3d-icon--chevron size-3 bg-gray-400 text-gray-400',
                      open ? '-rotate-90' : 'rotate-90'
                    )}
                  />
                </ComboboxButton>
              </div>
              {open && (
                <div className="mt-2.5 rounded-md border border-gray-300 bg-white p-1.5">
                  {filteredOptions.length === 0 ? (
                    <p role="status" data-testid="dealer-selection-no-match" className="px-2.5 py-1.5 text-sm">
                      <Trans>No Dealer ID matches your search.</Trans>
                    </p>
                  ) : (
                    <ComboboxOptions
                      modal={false}
                      className="flex max-h-[min(15rem,40vh)] flex-col gap-0.5 overflow-auto"
                    >
                      {filteredOptions.map((option) => {
                        const reasonId = `dealer-selection-option-reason-${option.dealerId}`;
                        const linkId = `dealer-selection-option-link-${option.dealerId}`;
                        return (
                          <ComboboxOption
                            key={option.dealerId}
                            value={option.dealerId}
                            disabled={Boolean(option.unavailableReason)}
                            data-testid={`dealer-selection-option-${option.dealerId}`}
                            className="flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-sm data-[disabled]:cursor-not-allowed data-[focus]:bg-blue-100 data-[focus]:font-semibold data-[selected]:font-semibold data-[disabled]:text-gray-500"
                          >
                            <span>{option.dealerId}</span>
                            {option.isFromLink && (
                              <span id={linkId} data-testid={linkId} className="text-xs text-blue-700">
                                <Trans>Selected</Trans>
                              </span>
                            )}
                            {option.unavailableReason && (
                              <span
                                id={reasonId}
                                data-testid={reasonId}
                                className="rounded-full bg-red-100 px-[7px] py-1 text-[11px] font-semibold text-red-700"
                              >
                                {t(getDealerUnavailableLabel(option.unavailableReason))}
                              </span>
                            )}
                          </ComboboxOption>
                        );
                      })}
                    </ComboboxOptions>
                  )}
                </div>
              )}
            </div>
            <button
              ref={okButtonRef}
              type="button"
              data-testid="dealer-selection-confirm"
              aria-disabled={!isSelectionAvailable}
              className="shrink-0 rounded-md bg-[var(--ex3d-dialog-confirm-bg-color,var(--ex3d-dealer-primary))] px-4 py-[9px] text-sm font-semibold text-[var(--ex3d-dialog-confirm-text-color,var(--ex3d-dealer-white))] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-0 focus-visible:outline-blue-700 aria-disabled:cursor-not-allowed aria-disabled:bg-gray-100 aria-disabled:text-gray-500"
              onClick={() => {
                if (isSelectionAvailable && selectedDealerId) {
                  onSelect(selectedDealerId);
                }
              }}
            >
              <Trans>OK</Trans>
            </button>
          </>
        )}
      </Combobox>
    </div>
  );
}
